import {
  Box,
  Chip,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Button,
} from "@material-ui/core";
import React from "react";
import moment from "moment/moment";
import { Cancel, CheckCircle } from "@material-ui/icons";
import GetAppIcon from "@material-ui/icons/GetApp";
import { SkeletonTableRow } from "components/skeleton/skeleton.component";
import RefreshTable from "components/table/refreshTable";
import { TablePaginationActions } from "components/tablePagination";
import { BarChart } from "modules/reporting/components/charts";
import Chart from "react-apexcharts";

const useStyles = makeStyles((theme) => ({
  content: {
    padding: "24px",
  },
  pageHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "24px",
  },
  pageTitle: {
    fontSize: "24px",
    fontWeight: 600,
    color: "#101828",
  },
  exportButton: {
    backgroundColor: "#2563EB",
    color: "#FFFFFF",
    textTransform: "none",
    fontWeight: 500,
    padding: "10px 20px",
    borderRadius: "8px",
    "&:hover": {
      backgroundColor: "#1D4ED8",
    },
  },
  chartsRow: {
    display: "flex",
    gap: "24px",
    marginBottom: "24px",
  },
  chartCard: {
    flex: 1,
    backgroundColor: "#FFFFFF",
    borderRadius: "12px",
    border: "1px solid #E4E7EC",
    padding: "24px",
  },
  chartTitle: {
    fontSize: "16px",
    fontWeight: 600,
    color: "#101828",
    marginBottom: "16px",
  },
  sectionTitle: {
    fontSize: "24px",
    fontWeight: 600,
    color: "#101828",
    padding: "24px 24px 0 24px",
  },
  pageContainer: {
    backgroundColor: "#FFFFFF",
    borderRadius: "12px",
    border: "1px solid #E4E7EC",
    margin: "16px 24px",
    overflow: "hidden",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "20px 24px",
    borderBottom: "1px solid #E4E7EC",
  },
  title: {
    fontSize: "18px",
    fontWeight: 600,
    color: "#101828",
  },
  searchWrapper: {
    marginLeft: "auto",
    display: "flex",
    alignItems: "center",
  },
  searchField: {
    width: "180px",
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px 0 0 8px",
      backgroundColor: "#FFFFFF",
      "& fieldset": {
        borderColor: "#D0D5DD",
        borderRight: "none",
      },
    },
    "& .MuiOutlinedInput-input": {
      padding: "10px 14px",
      fontSize: "14px",
    },
  },
  searchButton: {
    minWidth: "42px",
    height: "42px",
    padding: "0",
    borderRadius: "0 8px 8px 0",
    borderColor: "#D0D5DD",
    backgroundColor: "#FFFFFF",
    "&:hover": {
      backgroundColor: "#F9FAFB",
      borderColor: "#D0D5DD",
    },
    "& .MuiSvgIcon-root": {
      color: "#667085",
    },
  },
  filtersContainer: {
    display: "flex",
    gap: "16px",
    padding: "16px 24px",
    backgroundColor: "#F9FAFB",
    borderBottom: "1px solid #E4E7EC",
    flexWrap: "wrap",
    alignItems: "flex-end",
  },
  filterGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  filterLabel: {
    fontSize: "12px",
    fontWeight: 500,
    color: "#344054",
  },
  filterSelect: {
    minWidth: "160px",
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      backgroundColor: "#FFFFFF",
    },
    "& .MuiSelect-select": {
      padding: "10px 14px",
      fontSize: "14px",
    },
  },
  applyButton: {
    backgroundColor: "#1D4179",
    color: "#FFFFFF",
    textTransform: "none",
    fontWeight: 500,
    padding: "10px 20px",
    borderRadius: "8px",
    "&:hover": {
      backgroundColor: "#163362",
    },
  },
  tableContainer: {
    overflowX: "auto",
    "& .MuiTable-root": {
      minWidth: "900px",
    },
    "& .MuiTableCell-head": {
      backgroundColor: "#F9FAFB",
      fontWeight: 600,
      fontSize: "12px",
      color: "#667085",
      textTransform: "uppercase",
      padding: "12px 16px",
      borderBottom: "1px solid #E4E7EC",
      whiteSpace: "nowrap",
    },
    "& .MuiTableCell-body": {
      fontSize: "14px",
      color: "#344054",
      padding: "12px 16px",
      borderBottom: "1px solid #E4E7EC",
    },
  },
  expandCell: {
    width: "40px !important",
    minWidth: "40px !important",
    maxWidth: "40px !important",
    padding: "8px !important",
    textAlign: "center",
  },
  printJobLink: {
    color: "#1D4179",
    fontWeight: 500,
    cursor: "pointer",
    textDecoration: "none",
    "&:hover": {
      textDecoration: "underline",
    },
  },
  unusedValue: {
    color: "#F79009",
    fontWeight: 500,
  },
  deductedValue: {
    color: "#12B76A",
    fontWeight: 500,
  },
  reimbursedValue: {
    color: "#12B76A",
    fontWeight: 500,
  },
  // Credit Ledger styles
  ledgerContainer: {
    backgroundColor: "#F9FAFB",
    padding: "16px 24px",
    borderTop: "1px solid #E4E7EC",
  },
  ledgerTitle: {
    fontSize: "14px",
    fontWeight: 600,
    color: "#101828",
    marginBottom: "12px",
  },
  ledgerTable: {
    "& .MuiTableCell-head": {
      backgroundColor: "#FFFFFF",
      fontWeight: 600,
      fontSize: "11px",
      color: "#667085",
      textTransform: "uppercase",
      padding: "10px 12px",
      borderBottom: "1px solid #E4E7EC",
    },
    "& .MuiTableCell-body": {
      fontSize: "13px",
      color: "#344054",
      padding: "12px",
      borderBottom: "1px solid #E4E7EC",
      backgroundColor: "#FFFFFF",
    },
  },
  deductionChip: {
    backgroundColor: "#FEF3F2",
    color: "#B42318",
    fontWeight: 500,
    fontSize: "12px",
    height: "24px",
  },
  reimbursementChip: {
    backgroundColor: "#ECFDF3",
    color: "#027A48",
    fontWeight: 500,
    fontSize: "12px",
    height: "24px",
  },
  negativeQuantity: {
    color: "#B42318",
    fontWeight: 500,
  },
  positiveQuantity: {
    color: "#027A48",
    fontWeight: 500,
  },
  expandedRow: {
    backgroundColor: "#F9FAFB",
  },
  // QC Fail Qty - red color
  qcFailQty: {
    color: "#B42318",
    fontWeight: 600,
  },
  // Status chips
  disabledChip: {
    backgroundColor: "#FEF3F2",
    color: "#B42318",
    fontWeight: 500,
    fontSize: "12px",
  },
  softDeletedChip: {
    backgroundColor: "#FFFAEB",
    color: "#B54708",
    fontWeight: 500,
    fontSize: "12px",
  },
  // QC Type chips
  qcTypeHighChip: {
    backgroundColor: "#ECFDF3",
    color: "#027A48",
    fontWeight: 500,
    fontSize: "12px",
  },
  qcTypeLowChip: {
    backgroundColor: "#F2F4F7",
    color: "#344054",
    fontWeight: 500,
    fontSize: "12px",
  },
  // Reimbursed row
  reimbursedRow: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },
  reimbursedNo: {
    color: "#B42318",
    fontSize: "14px",
  },
  reimbursedYes: {
    color: "#027A48",
    fontSize: "14px",
  },
  reimbursedIconNo: {
    color: "#B42318",
    fontSize: "18px",
  },
  reimbursedIconYes: {
    color: "#027A48",
    fontSize: "18px",
  },
  emptyContainer: {
    textAlign: "center",
    padding: "48px 24px",
  },
}));

export default function QcImpactAnalysisPage({
  translate,
  listingData,
  pagination,
  onPageChange,
  onRowsPerPageChange,
  isLoading,
  isError,
  handleReload,
  onExport,
  qcFailByProduct,
  qcImpactByMonth,
  paginationOptions = [25, 50, 100],
}) {
  const classes = useStyles();

  const records = listingData?.records || [];

  return (
    <Box className={classes.content}>
      {/* Page Header */}
      <Box className={classes.pageHeader}>
        <Typography className={classes.pageTitle}>
          {translate("label.QC_IMPACT_ANALYSIS")}
        </Typography>
        <Button
          variant="contained"
          className={classes.exportButton}
          startIcon={<GetAppIcon />}
          onClick={onExport}
        >
          {translate("label.EXPORT_QC_REPORT")}
        </Button>
      </Box>

      {/* Charts Row */}
      <Box className={classes.chartsRow}>
        {/* QC Fail Quantity by Product - Pie Chart */}
        <Box className={classes.chartCard}>
          <Typography className={classes.chartTitle}>
            {translate("label.QC_FAIL_QUANTITY_BY_PRODUCT")}
          </Typography>
          {qcFailByProduct &&
            qcFailByProduct.pieTotal &&
            qcFailByProduct.pieTotal.length > 0 && (
              <Chart
                type="pie"
                height={350}
                series={qcFailByProduct.pieTotal}
                options={{
                  chart: {
                    animations: {
                      enabled: false,
                    },
                  },
                  labels: qcFailByProduct.pieLabels,
                  colors: [
                    "#10B981",
                    "#3B82F6",
                    "#EF4444",
                    "#F59E0B",
                    "#6B7280",
                  ],
                  legend: {
                    position: "bottom",
                    horizontalAlign: "center",
                  },
                  dataLabels: {
                    enabled: true,
                    formatter: function (val) {
                      return val.toFixed(1) + "%";
                    },
                    style: {
                      fontSize: "14px",
                      fontWeight: "bold",
                      colors: ["#FFFFFF"],
                    },
                    dropShadow: {
                      enabled: false,
                    },
                  },
                  plotOptions: {
                    pie: {
                      dataLabels: {
                        offset: -15,
                      },
                    },
                  },
                  tooltip: {
                    y: {
                      formatter: function (val) {
                        return val.toFixed(1) + "%";
                      },
                    },
                  },
                }}
              />
            )}
        </Box>

        {/* QC Impact by Month - Bar Chart */}
        <Box className={classes.chartCard}>
          <Typography className={classes.chartTitle}>
            {translate("label.QC_IMPACT_BY_MONTH")}
          </Typography>
          {qcImpactByMonth &&
            qcImpactByMonth.datasets &&
            qcImpactByMonth.datasets.length > 0 && (
              <BarChart
                payload={qcImpactByMonth}
                chartColors={["#EF4444"]}
                translate={translate}
                chartHeight={350}
              />
            )}
        </Box>
      </Box>

      {/* QC Event History Table */}
      <Box className={classes.pageContainer}>
        {/* Header */}
        <Box className={classes.header}>
          <Typography className={classes.title}>
            {translate("label.QC_EVENT_HISTORY")}
          </Typography>
        </Box>

        {/* Table */}
        <TableContainer className={classes.tableContainer}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{translate("label.QC_EVENT_DATE")}</TableCell>
                <TableCell>{translate("label.PRINT_JOB_ID")}</TableCell>
                <TableCell>{translate("label.PRINT_JOB_DATE")}</TableCell>
                <TableCell>{translate("label.PRODUCT_SKU")}</TableCell>
                <TableCell align="right">
                  {translate("label.QC_FAIL_QTY")}
                </TableCell>
                <TableCell>{translate("label.QR_STATUS")}</TableCell>
                <TableCell align="right">
                  {translate("label.QC_TYPE")}
                </TableCell>
                <TableCell>{translate("label.REIMBURSED")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading
                ? [...new Array(5)].map((_, index) => (
                    <SkeletonTableRow key={index} columnCount={8} />
                  ))
                : records.map((row, index) => (
                    <TableRow hover key={`${row.print_job_id}-${index}`}>
                      <TableCell>
                        <span className={classes.printJobLink}>
                          {moment(row.created_at).format("ll")}
                        </span>
                      </TableCell>
                      <TableCell>{row.print_job_id}</TableCell>
                      <TableCell>
                        {row.printed_at
                          ? moment(row.printed_at).format("ll")
                          : "-"}
                      </TableCell>
                      <TableCell>{row.product_sku}</TableCell>
                      <TableCell align="right">
                        <span className={classes.qcFailQty}>
                          {row.quantity.toLocaleString()}
                        </span>
                      </TableCell>
                      <TableCell>
                        {row.can_reimburse ? (
                          <Chip
                            label={translate("label.SOFT_DELETED")}
                            className={classes.softDeletedChip}
                            size="small"
                          />
                        ) : (
                          <Chip
                            label={translate("label.DISABLED")}
                            className={classes.disabledChip}
                            size="small"
                          />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {row.can_reimburse ? (
                          <Chip
                            label="≥1000"
                            className={classes.qcTypeHighChip}
                            size="small"
                          />
                        ) : (
                          <Chip
                            label="<1000"
                            className={classes.qcTypeLowChip}
                            size="small"
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Box className={classes.reimbursedRow}>
                          {row.status === 0 ? (
                            <>
                              <Cancel className={classes.reimbursedIconNo} />
                              <Typography className={classes.reimbursedNo}>
                                {translate("label.NO")}
                              </Typography>
                            </>
                          ) : (
                            <>
                              <CheckCircle
                                className={classes.reimbursedIconYes}
                              />
                              <Typography className={classes.reimbursedYes}>
                                {translate("label.YES")}
                              </Typography>
                            </>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Empty State */}
        {!isLoading && !isError && records.length === 0 && (
          <Box className={classes.emptyContainer}>
            <Typography variant="body1">
              {translate("paragraph.NO_QC_EVENT_HISTORY")}
            </Typography>
          </Box>
        )}

        {/* Error State */}
        {!isLoading && isError && <RefreshTable handleReload={handleReload} />}

        {/* Pagination */}
        {!isLoading && !isError && (
          <TablePagination
            ActionsComponent={TablePaginationActions}
            rowsPerPageOptions={paginationOptions}
            component="div"
            count={listingData?.totalRecords || 0}
            rowsPerPage={pagination?.rowsPerPage || 25}
            page={pagination?.page || 0}
            onChangePage={(e, newPage) => onPageChange && onPageChange(newPage)}
            onChangeRowsPerPage={(e) =>
              onRowsPerPageChange &&
              onRowsPerPageChange(parseInt(e.target.value, 10))
            }
          />
        )}
      </Box>
    </Box>
  );
}
